import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://apigw-trident.onified.ai';

// Shared axios instance so we get a single 15s timeout for every form submit
// rather than relying on the default (no timeout). Long-hanging connections
// previously left the UI in "Sending…" forever.
const contactApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'X-Source': 'technum',
  },
});

const extractBackendMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.message) return data.message;
  if (data?.error) return typeof data.error === 'string' ? data.error : data.error.message;
  if (Array.isArray(data?.errors) && data.errors.length) {
    const first = data.errors[0];
    if (typeof first === 'string') return first;
    if (first?.message) return first.message;
  }
  if (error?.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (error?.message) return error.message;
  return 'Something went wrong. Please try again.';
};

export const PostContact = createAsyncThunk(
  'Contact/PostContact',
  async ({ payload, idempotencyKey, turnstileToken } = {}, { rejectWithValue }) => {
    try {
      const headers = {};
      if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
      // Optional: invisible Cloudflare Turnstile. Backend must verify when present.
      if (turnstileToken) headers['cf-turnstile-response'] = turnstileToken;

      const response = await contactApi.post('/api/v1/auth/submit', payload, { headers });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractBackendMessage(error));
    }
  }
);

/** @deprecated Use PostContact instead */
export const PostConatact = PostContact;

const contactSlice = createSlice({
  name: 'contact',
  initialState: {
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetContactState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(PostContact.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(PostContact.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(PostContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { resetContactState } = contactSlice.actions;
export default contactSlice.reducer;
